import type { components } from "@patent-studio/api-types";
import { bijectiveBase26 } from "./bijective-base-26";

export type FigureNode = components["schemas"]["FigureNodeModel"];
export type FigureGroup = components["schemas"]["FigureGroupModel"];
export type FigureLayout = components["schemas"]["FigureLayoutRequest"];

/** Letters for a member's position, zero-based and upper case, as a figure is labelled. */
function memberSuffix(index: number): string {
  return bijectiveBase26(index + 1).toUpperCase();
}

/**
 * Label every figure the layout places, keyed by file identifier.
 *
 * A standalone figure takes the next number; a group takes one number and
 * letters its members. An empty group takes none: it stays, being the
 * attorney's, but numbering it would leave a gap in the figure sequence, and a
 * gap is a formal defect in the application. An unplaced file gets no label.
 *
 * Runs here as well as on the server so that rearranging re-letters without a
 * round trip. The two must agree exactly, which is what the shared cases in
 * `packages/contracts/fixtures/figure-labels.json` hold them to.
 */
export function projectFigureLabels(
  // The entries alone. A label follows from the arrangement, so asking for the
  // whole layout would make a caller carry a version it has no use for.
  layout: Pick<FigureLayout, "entries">,
): Record<string, string> {
  const labels: Record<string, string> = {};
  let figureNumber = 0;

  for (const entry of layout.entries ?? []) {
    if (entry.kind === "figure") {
      figureNumber += 1;
      labels[entry.file_id] = String(figureNumber);
      continue;
    }
    const members = entry.members ?? [];
    if (members.length === 0) {
      continue;
    }
    figureNumber += 1;
    members.forEach((member, index) => {
      labels[member.file_id] = `${figureNumber}${memberSuffix(index)}`;
    });
  }

  return labels;
}

/**
 * Every file the arrangement places, in set order.
 *
 * The one walk of the entry tree; the review sections, the sheet packing and
 * the layout hook all answer "which figures does this layout place" through it
 * so the empty-group edge cannot drift between them.
 */
export function placedFileIds(entries: FigureLayout["entries"]): string[] {
  return (entries ?? []).flatMap((entry) =>
    entry.kind === "figure"
      ? [entry.file_id]
      : (entry.members ?? []).map((member) => member.file_id),
  );
}
